import { useEffect, useState } from 'react';
import { addComma } from 'constants/utils';
import { ICalculatePaystubDeductionRes, IGetFixedDataRes, IGetPaystubPayInfoRes } from 'types/paystub';
import useApi from 'hooks/useApi';
import useErrorLog from 'hooks/useErrorLog';
import useLoadingStore from 'zustands/useLoadingStore';
import { AxiosResponse } from 'axios';

const PaystubPreview = ({
  id,
  deduction,
  payment,
  setPdfUrl,
}: {
  id?: string;
  deduction?: ICalculatePaystubDeductionRes;
  payment?: IGetPaystubPayInfoRes['payment']['payment_items'];
  setPdfUrl?: (url: string) => void;
}) => {
  const { webApiNode } = useApi();
  const { checkErrorLog } = useErrorLog();
  const { setLoadingState } = useLoadingStore();
  const [previewInfo, setPreviewInfo] = useState<IGetFixedDataRes['resultData']>([]);

  const [myInfo, setMyInfo] = useState<ICalculatePaystubDeductionRes['empInfo']>({
    EMP_NAME: '',
    BIRTHDATE: '',
    STORE_NAME: '',
    PAYMENT_TYPE: '',
    TAX_TYPE: '',
    PAY_DAY: '',
    CALCULATE_PERIOD: '',
    payment_amount: '',
    send_time: '',
  });

  const [deductionData, setDeductionData] = useState<ICalculatePaystubDeductionRes | null>(null);
  const [paymentData, setPaymentData] = useState<IGetPaystubPayInfoRes['payment']['payment_items']>([]);
  const [totalDeduction, setTotalDeduction] = useState(0);

  const fetchPaystubSendDetail = async (id: string) => {
    if (!id) return;
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/paystub/getPaystubSendDetail`, {
        id: id ? id.toString().split(' ').join('+') : '',
      });
      if (data.message === 'SUCCESS') {
        const resultData = data.resultData;
        setMyInfo({
          ...resultData.empInfo,
          PAYMENT_TYPE: resultData.empInfo.PAYMENT_TYPE,
        });
        setPaymentData(
          resultData.payment_items.map((item: any) => ({
            name: item.name,
            value: Number(item.value.replace(/,/g, '').replace(/원/g, '')),
            type: item.type,
          })),
        );

        // ICalculatePaystubDeductionRes 형식으로 변환
        setDeductionData({
          basic_items: resultData.basic_items,
          payment_items: resultData.payment_items.map((item: any) => ({
            name: item.name,
            value: item.value,
            pay_value: Number(item.value.replace(/,/g, '').replace(/원/g, '')),
            formula: item.formula,
            type: item.type,
          })),
          total_pay: resultData.total_pay,
          deduction_items: resultData.deduction_items.map((item: any) => ({
            name: item.name,
            value: item.value,
            pay_value: Number(item.value.replace(/,/g, '').replace(/원/g, '')),
            edit_type: 'DEFAULT' as const,
            formula: item.formula,
          })),
          empInfo: resultData.empInfo,
        });
        const totalDeduction = resultData.deduction_items.reduce(
          (sum: number, item: any) => sum + Number(item.value.replace(/,/g, '').replace(/원/g, '')),
          0,
        );

        setTotalDeduction(totalDeduction);
        if (setPdfUrl) {
          setPdfUrl(data.resultData.PDF_URL);
        }
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, 'paystub/getPaystubSendDetail');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };
  const fetchFixedData = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('get', `/paystub/getFixedData`);

      if (data.message === 'SUCCESS') {
        setPreviewInfo(data.resultData);
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, 'paystub/getFixedData');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  useEffect(() => {
    if (payment) {
      setPaymentData(payment);
    }
  }, [payment]);

  useEffect(() => {
    fetchFixedData();
  }, []);

  useEffect(() => {
    if (id) {
      fetchPaystubSendDetail(id);
    }
  }, [id]);

  useEffect(() => {
    if (deduction) {
      const totalDeduction = deduction.deduction_items.reduce((sum, item) => sum + item.pay_value, 0);

      const actualPayment = parseFloat(deduction.total_pay.replace(/,/g, '')) - totalDeduction;

      setMyInfo({
        ...deduction.empInfo,
        payment_amount: addComma(actualPayment.toString()) + '원',
      });
      setDeductionData(deduction);

      setTotalDeduction(totalDeduction);
    }
  }, [deduction]);

  return (
    <div className="flex w-full max-w-[540px] flex-col  gap-6 bg-[#F3F4F6] p-4 sm:p-8">
      {/* 내 정보 섹션 */}
      <div className="flex flex-col gap-4">
        <p className="text-base font-semibold leading-6 text-[#898989]">내 정보</p>
        <div className="overflow-hidden rounded-lg border border-[#d9d9d9] bg-white">
          <div className="flex border-b border-[#d9d9d9]">
            <div className="flex min-h-[40px] w-[35%] min-w-[90px] items-center justify-center border-r border-[#d9d9d9] bg-[#f4f4f4] px-2 py-2 sm:px-3">
              <p className="break-words text-center text-sm font-semibold text-[#111]">이름</p>
            </div>
            <div className="flex min-h-[40px] min-w-0 flex-1 items-center px-2 py-2 sm:px-3">
              <p className="break-words text-sm text-[#111]">{myInfo.EMP_NAME}</p>
            </div>
          </div>
          <div className="flex border-b border-[#d9d9d9]">
            <div className="flex min-h-[40px] w-[35%] min-w-[90px] items-center justify-center border-r border-[#d9d9d9] bg-[#f4f4f4] px-2 py-2 sm:px-3">
              <p className="break-words text-center text-sm font-semibold text-[#111]">생년월일</p>
            </div>
            <div className="flex min-h-[40px] min-w-0 flex-1 items-center px-2 py-2 sm:px-3">
              <p className="break-words text-sm text-[#111]">{myInfo.BIRTHDATE}</p>
            </div>
          </div>
          <div className="flex border-b border-[#d9d9d9]">
            <div className="flex min-h-[40px] w-[35%] min-w-[90px] items-center justify-center border-r border-[#d9d9d9] bg-[#f4f4f4] px-2 py-2 sm:px-3">
              <p className="break-words text-center text-sm font-semibold text-[#111]">사업장명</p>
            </div>
            <div className="flex min-h-[40px] min-w-0 flex-1 items-center px-2 py-2 sm:px-3">
              <p className="break-words text-sm text-[#111]">{myInfo.STORE_NAME}</p>
            </div>
          </div>
          <div className="flex border-b border-[#d9d9d9]">
            <div className="flex min-h-[40px] w-[35%] min-w-[90px] items-center justify-center border-r border-[#d9d9d9] bg-[#f4f4f4] px-2 py-2 sm:px-3">
              <p className="break-words text-center text-sm font-semibold text-[#111]">실지급액</p>
            </div>
            <div className="flex min-h-[40px] min-w-0 flex-1 items-center px-2 py-2 sm:px-3">
              <p className="break-words text-sm text-[#111]">{myInfo.payment_amount}</p>
            </div>
          </div>
          <div className="flex border-b border-[#d9d9d9]">
            <div className="flex min-h-[40px] w-[35%] min-w-[90px] items-center justify-center border-r border-[#d9d9d9] bg-[#f4f4f4] px-2 py-2 sm:px-3">
              <p className="break-words text-center text-sm font-semibold text-[#111]">급여유형</p>
            </div>
            <div className="flex min-h-[40px] min-w-0 flex-1 items-center px-2 py-2 sm:px-3">
              <p className="break-words text-sm text-[#111]">{myInfo.PAYMENT_TYPE}</p>
            </div>
          </div>
          <div className="flex border-b border-[#d9d9d9]">
            <div className="flex min-h-[40px] w-[35%] min-w-[90px] items-center justify-center border-r border-[#d9d9d9] bg-[#f4f4f4] px-2 py-2 sm:px-3">
              <p className="break-words text-center text-sm font-semibold text-[#111]">세금유형</p>
            </div>
            <div className="flex min-h-[40px] min-w-0 flex-1 items-center px-2 py-2 sm:px-3">
              <p className="break-words text-sm text-[#111]">{myInfo.TAX_TYPE}</p>
            </div>
          </div>
          <div className="flex border-b border-[#d9d9d9]">
            <div className="flex min-h-[40px] w-[35%] min-w-[90px] items-center justify-center border-r border-[#d9d9d9] bg-[#f4f4f4] px-2 py-2 sm:px-3">
              <p className="break-words text-center text-sm font-semibold text-[#111]">급여지급일</p>
            </div>
            <div className="flex min-h-[40px] min-w-0 flex-1 items-center px-2 py-2 sm:px-3">
              <p className="break-words text-sm text-[#111]">{myInfo.PAY_DAY}</p>
            </div>
          </div>
          <div className="flex border-b border-[#d9d9d9]">
            <div className="flex min-h-[40px] w-[35%] min-w-[90px] items-center justify-center border-r border-[#d9d9d9] bg-[#f4f4f4] px-2 py-2 sm:px-3">
              <p className="break-words text-center text-sm font-semibold text-[#111]">급여 정산 기간</p>
            </div>
            <div className="flex min-h-[40px] min-w-0 flex-1 items-center px-2 py-2 sm:px-3">
              <p className="break-words text-sm text-[#111]">{myInfo.CALCULATE_PERIOD}</p>
            </div>
          </div>
          <div className="flex">
            <div className="flex min-h-[40px] w-[35%] min-w-[90px] items-center justify-center border-r border-[#d9d9d9] bg-[#f4f4f4] px-2 py-2 sm:px-3">
              <p className="break-words text-center text-sm font-semibold text-[#111]">전송 시간</p>
            </div>
            <div className="flex min-h-[40px] min-w-0 flex-1 items-center px-2 py-2 sm:px-3">
              <p className="break-words text-sm text-[#111]">{myInfo.send_time}</p>
            </div>
          </div>
        </div>
      </div>

      {/* 지급 내역 섹션 */}
      <div className="flex flex-col gap-4">
        <p className="text-base font-semibold leading-6 text-[#898989]">지급 내역</p>
        <div className="overflow-hidden rounded-lg border border-[#d9d9d9] bg-white">
          {/* Header */}
          <div className="flex border-b border-[#d9d9d9] bg-[#f4f4f4]">
            <div className="flex min-h-[40px] w-[25%] min-w-[70px] items-center justify-center border-r border-[#d9d9d9] px-2 py-2 sm:px-3">
              <p className="break-words text-center text-sm font-semibold text-[#111]">항목</p>
            </div>
            <div className="flex min-h-[40px] w-[45%] min-w-[100px] items-center justify-center border-r border-[#d9d9d9] px-2 py-2 sm:px-3">
              <p className="break-words text-center text-sm font-semibold text-[#111]">산출식</p>
            </div>
            <div className="flex min-h-[40px] min-w-0 flex-1 items-center justify-center px-2 py-2 sm:px-3">
              <p className="break-words text-center text-sm font-semibold text-[#111]">값</p>
            </div>
          </div>
          {/* Payment Items */}
          {deductionData?.basic_items.map((item, index) => (
            <div key={`deductionData-basic_items-${index}`} className="flex border-b border-[#d9d9d9]">
              <div className="flex min-h-[40px] w-[25%] min-w-[70px] items-center border-r border-[#d9d9d9] px-2 py-2 sm:px-3">
                <p className="break-words text-sm text-[#111]">{item.name}</p>
              </div>
              <div className="flex min-h-[40px] w-[45%] min-w-[100px] items-center justify-center border-r border-[#d9d9d9] px-2 py-2 sm:px-3">
                <p className="break-words text-center text-xs text-[#767676]">{item.formula}</p>
              </div>
              <div className="flex min-h-[40px] min-w-0 flex-1 items-center justify-end px-2 py-2 sm:px-3">
                <p className="break-words text-sm text-[#111]">{addComma(item.value.toString())}</p>
              </div>
            </div>
          ))}
          {paymentData.map((item, index) => (
            <div key={`paymentData-${index}`} className="flex border-b border-[#d9d9d9]">
              <div className="flex min-h-[40px] w-[25%] min-w-[70px] items-center border-r border-[#d9d9d9] px-2 py-2 sm:px-3">
                <p className="break-words text-sm text-[#111]">{item.name}</p>
              </div>
              <div className="flex min-h-[40px] w-[45%] min-w-[100px] items-center justify-center border-r border-[#d9d9d9] px-2 py-2 sm:px-3">
                <p className="break-words text-center text-xs text-[#767676]">
                  {item.type === 'NON_TAXABLE' ? '비과세' : '-'}
                </p>
              </div>
              <div className="flex min-h-[40px] min-w-0 flex-1 items-center justify-end px-2 py-2 sm:px-3">
                <p className="break-words text-sm text-[#111]">{addComma(item.value.toString())}원</p>
              </div>
            </div>
          ))}
          {/* Total */}
          <div className="flex">
            <div className="flex min-h-[40px] w-[70%] min-w-0 items-center justify-center border-r border-[#d9d9d9] px-2 py-2 sm:px-3">
              <p className="break-words text-center text-sm font-semibold text-[#111]">지급액 합계</p>
            </div>
            <div className="flex min-h-[40px] min-w-0 flex-1 items-center justify-end px-2 py-2 sm:px-3">
              <p className="break-words text-sm font-semibold text-[#111]">
                {addComma(deductionData?.total_pay.toString() || '0')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 공제내역 섹션 */}
      <div className="flex flex-col gap-4">
        <p className="text-base font-semibold leading-6 text-[#898989]">공제내역</p>
        <div className="overflow-hidden rounded-lg border border-[#d9d9d9] bg-white">
          {/* Header */}
          <div className="flex border-b border-[#d9d9d9] bg-[#f4f4f4]">
            <div className="flex min-h-[40px] w-[25%] min-w-[70px] items-center justify-center border-r border-[#d9d9d9] px-2 py-2 sm:px-3">
              <p className="break-words text-center text-sm font-semibold text-[#111]">항목</p>
            </div>
            <div className="flex min-h-[40px] w-[45%] min-w-[100px] items-center justify-center border-r border-[#d9d9d9] px-2 py-2 sm:px-3">
              <p className="break-words text-center text-sm font-semibold text-[#111]">산출식</p>
            </div>
            <div className="flex min-h-[40px] min-w-0 flex-1 items-center justify-center px-2 py-2 sm:px-3">
              <p className="break-words text-center text-sm font-semibold text-[#111]">값</p>
            </div>
          </div>
          {/* Deduction Items */}
          {deductionData?.deduction_items.map((item, index) => (
            <div key={`deductionData-deduction_items-${index}`} className="flex border-b border-[#d9d9d9]">
              <div className="flex min-h-[40px] w-[25%] min-w-[70px] items-center border-r border-[#d9d9d9] px-2 py-2 sm:px-3">
                <p className="break-words text-sm text-[#111]">{item.name}</p>
              </div>
              <div className="flex min-h-[40px] w-[45%] min-w-[100px] items-center justify-center border-r border-[#d9d9d9] px-2 py-2 sm:px-3">
                <p className="break-words text-center text-xs text-[#767676]">{item.formula}</p>
              </div>
              <div className="flex min-h-[40px] min-w-0 flex-1 items-center justify-end px-2 py-2 sm:px-3">
                <p className="break-words text-sm text-[#111]">{item.value}</p>
              </div>
            </div>
          ))}
          {/* Total */}
          <div className="flex">
            <div className="flex min-h-[40px] w-[70%] min-w-0 items-center justify-center border-r border-[#d9d9d9] px-2 py-2 sm:px-3">
              <p className="break-words text-center text-sm font-semibold text-[#111]">공제액 합계</p>
            </div>
            <div className="flex min-h-[40px] min-w-0 flex-1 items-center justify-end px-2 py-2 sm:px-3">
              <p className="break-words text-sm font-semibold text-[#111]">{addComma(totalDeduction.toString())}원</p>
            </div>
          </div>
        </div>
      </div>

      {/* 안내 박스 */}
      <div className="flex w-full max-w-[540px] flex-col gap-3 rounded-lg bg-[#f4f4f4] px-3 py-4 sm:px-2.5 sm:py-5">
        {previewInfo.map((info, index) => (
          <div key={index} className="flex flex-col gap-[3px]">
            <p className="break-words text-xs font-bold leading-[16.8px] text-[#404040]">{info.type}</p>
            <div className="flex gap-[6px]">
              <div className="mt-[6px] h-[3px] w-[3px] flex-shrink-0 rounded-full bg-[#404040]"></div>
              <p
                className="flex-1 break-words text-xs leading-[16.8px] text-[#404040]"
                style={{ whiteSpace: 'normal' }}>
                {info.contents}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PaystubPreview;
